import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  Autocomplete,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  AutoAwesome,
  CloudUpload,
  UploadFile,
} from '@mui/icons-material';
import api from '../config/api';
import * as mammoth from 'mammoth';
import useStore from '../config/store';
import { useTranslation } from 'react-i18next';

const PositionsModal = ({
  open,
  onClose,
  mode, // 'create', 'edit', 'view'
  selectedPosition,
  onSave,
  user,
  authToken,
  showAlert,
  project // Agregar project como prop
}) => {
  const [formData, setFormData] = useState({
    name: '',
    industria: '',
    descripcion: '',
    jerarquia: '',
    responsabilidades: '',
    perfil: '',
    conocimientosExcluyentes: '',
    conocimientosDeseables: '',
    salarioOfrecido: '' // AGREGADO
  });
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [loading, setLoading] = useState(false);
  const [gptLoading, setGptLoading] = useState(false);
  const [docxLoading, setDocxLoading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // Competencias predeterminadas del cargo (2026-09-09). Se guardan como
  // [{ competencyId, competencyName }] — misma forma que envían los tests DISC —
  // y viven fuera de formData para que los reemplazos completos del formulario
  // (GPT, plantilla .docx) no las pisen.
  const [selectedCompetencias, setSelectedCompetencias] = useState([]);
  const [competenciesOptions, setCompetenciesOptions] = useState([]);
  const [loadingCompetencies, setLoadingCompetencies] = useState(false);

  const { language, organization } = useStore.getState();
  const { t } = useTranslation();

  // Cargar catálogo de competencias al abrir (mismo patrón que DiscConfiguration)
  useEffect(() => {
    const loadCompetencies = async () => {
      if (!authToken || !organization) return;
      try {
        setLoadingCompetencies(true);
        const response = await api.get('/capabilities', {
          params: { language, organizationId: organization },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (response.data.errorCode === 0 && response.data.response?.data) {
          setCompetenciesOptions(response.data.response.data);
        }
      } catch (error) {
        console.error('Error loading competencies:', error);
      } finally {
        setLoadingCompetencies(false);
      }
    };

    if (open) {
      loadCompetencies();
    }
  }, [open, authToken, organization, language]);

  const normalizeCompetencia = (c) => ({
    competencyId: c.competencyId ?? c.id ?? c.uid,
    competencyName: c.competencyName ?? c.name ?? '',
  });

  // Opciones predefinidas para industrias
  const industriasOptions = t('industries', { returnObjects: true })

  // API Functions
  const createPosition = async (positionData) => {
    const response = await api.post("/positions", positionData, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return response.data;
  };

  const updatePosition = async (id, positionData) => {
    const response = await api.put(`/positions/${id}`, positionData, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return response.data;
  };

  // Función para generar descripción con GPT
  const generateDescriptionWithGPT = async () => {
    try {
      setGptLoading(true);

      if (!formData.name.trim() || !formData.industria.trim() || !formData.jerarquia.trim()) {
        showAlert(t('positions.completeNameAndIndustry'), "warning");
        return;
      }

      const requestData = {
        positionName: formData.name.trim(),
        industry: formData.industria.trim(),
        jerarquia: formData.jerarquia.trim(),
        language,
        projectId: project,
        organizationId: organization,
        referenceId: selectedPosition?.uid || null
      };

      const response = await api.post("/gpt-agent/generate-position-description", requestData, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (response.data.response && response.data.response.status_response === 'OK') {
        const data = response.data.response.data;

        // REEMPLAZAR todo el formulario con los datos generados por GPT
        setFormData({
          name: formData.name, // Mantener nombre que usuario ingresó
          industria: formData.industria, // Mantener industria que usuario ingresó  
          jerarquia: formData.jerarquia, // Mantener jerarquía que usuario ingresó
          salarioOfrecido: formData.salarioOfrecido, // AGREGADO - Mantener salario
          descripcion: data.description || '',
          responsabilidades: data.responsabilidades || '',
          perfil: data.perfil || '',
          conocimientosExcluyentes: data.conocimientosExcluyentes || '',
          conocimientosDeseables: data.conocimientosDeseables || ''
        });

        showAlert(t('positions.generateDescriptionSuccess'), "success");
      } else {
        showAlert(t('positions.generateDescriptionError'), "error");
      }
    } catch (err) {
      showAlert(t('generalApiError'), "error");
      console.error('Error generating description:', err);
    } finally {
      setGptLoading(false);
    }
  };

  // Función para procesar plantilla DOCX
  const processDocxTemplate = async (file) => {
    try {
      setDocxLoading(true);

      // Leer archivo Word
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      const text = result.value;

      // Patrones para español e inglés (igual que antes)
      const patterns = {
        es: {
          name: /Nombre del cargo:\s*(.+?)(?:\n|$)/i,
          industria: /Industria:\s*(.+?)(?:\n|$)/i,
          jerarquia: /Jerarquía:\s*(.+?)(?:\n|$)/i,
          descripcion: /Misión\s*(.+?)(?=###|Principales responsabilidades|$)/is,
          responsabilidades: /Principales responsabilidades\s*(.+?)(?=###|Perfil General|$)/is,
          perfil: /Perfil General\s*(.+?)(?=###|Conocimientos excluyentes|$)/is,
          conocimientosExcluyentes: /Conocimientos excluyentes\s*(.+?)(?=###|Conocimientos deseables|$)/is,
          conocimientosDeseables: /Conocimientos deseables\s*(.+?)(?=###|$)/is
        },
        en: {
          name: /Job Title:\s*(.+?)(?:\n|$)/i,
          industria: /Industry:\s*(.+?)(?:\n|$)/i,
          jerarquia: /Hierarchy Level:\s*(.+?)(?:\n|$)/i,
          descripcion: /Mission\s*(.+?)(?=###|Main Responsibilities|$)/is,
          responsabilidades: /Main Responsibilities\s*(.+?)(?=###|General Profile|$)/is,
          perfil: /General Profile\s*(.+?)(?=###|Mandatory Knowledge|$)/is,
          conocimientosExcluyentes: /Mandatory Knowledge\s*(.+?)(?=###|Desired Knowledge|$)/is,
          conocimientosDeseables: /Desired Knowledge\s*(.+?)(?=###|$)/is
        }
      };

      const currentPatterns = patterns[language] || patterns.es;

      // Extraer y limpiar campos
      const extractedData = {};
      Object.keys(currentPatterns).forEach(key => {
        const match = text.match(currentPatterns[key]);
        if (match) {
          let value = match[1].trim();
          value = value.replace(/^[-•*]\s*/gm, '');
          value = value.replace(/\n\s*\n/g, '\n');
          value = value.replace(/\s+/g, ' ');
          extractedData[key] = value;
        } else {
          extractedData[key] = '';
        }
      });

      // REEMPLAZAR todo el formulario con los datos extraídos
      setFormData({
        name: extractedData.name || '',
        industria: extractedData.industria || '',
        descripcion: extractedData.descripcion || '',
        jerarquia: extractedData.jerarquia || '',
        responsabilidades: extractedData.responsabilidades || '',
        perfil: extractedData.perfil || '',
        conocimientosExcluyentes: extractedData.conocimientosExcluyentes || '',
        conocimientosDeseables: extractedData.conocimientosDeseables || '',
        salarioOfrecido: formData.salarioOfrecido // AGREGADO - Mantener salario
      });

      showAlert(t('positions.templateProcessedSuccess'), "success");
    } catch (err) {
      showAlert(t('positions.templateProcessError'), "error");
      console.error('Error processing Word template:', err);
    } finally {
      setDocxLoading(false);
    }
  };

  // Función para descargar plantilla
  const downloadTemplate = () => {
    const templateName = language === 'en'
      ? 'Job_Description_Template.docx'
      : 'Modelo_de_Descriptor_del_cargo.docx';

    // En un entorno real, estos archivos estarían en /public o en un CDN
    const link = document.createElement('a');
    link.href = `/templates/${templateName}`;
    link.download = templateName;
    link.click();
  };

  // Manejar selección de archivo DOCX
  const handleFileSelect = (file) => {
    const isWordFile = file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.type === 'application/msword';

    if (file && isWordFile) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        showAlert(t('maxFileSize'), "error");
        return;
      }
      processDocxTemplate(file);
    } else {
      showAlert(t('positions.selectValidWord'), "error");
    }
  };

  // Manejar input de archivo
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
    // Limpiar el input para permitir seleccionar el mismo archivo nuevamente
    e.target.value = '';
  };

  // Funciones para drag and drop en el textarea
  const handleTextareaDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Solo activar si hay archivos Word en el drag
    const files = Array.from(e.dataTransfer.files || e.dataTransfer.items);
    const hasWord = files.some(item => {
      return (item.type && (
        item.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        item.type === 'application/msword'
      )) || (item.kind === 'file' && (
        item.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        item.type === 'application/msword'
      ));
    });

    if (hasWord || e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true);
    }
  };

  const handleTextareaDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleTextareaDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Solo quitar el drag over si realmente salimos del componente
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleTextareaDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const wordFile = files.find(file =>
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.type === 'application/msword'
    );

    if (wordFile) {
      handleFileSelect(wordFile);
    } else if (files.length > 0) {
      showAlert(t('positions.selectValidWord'), "warning");
    }
  };

  // Validaciones
  const validateForm = () => {
    const errors = [];

    // Validar nombre
    if (!formData.name || formData.name.trim() === '') {
      errors.push(t('positions.validateNameExist'));
    }

    // Validar industria
    if (!formData.industria || formData.industria.trim() === '') {
      errors.push(t('positions.validateIndustryExist'));
    }

    // Validar descripción/misión (ahora es obligatoria)
    if (!formData.descripcion || formData.descripcion.trim() === '') {
      errors.push(t('positions.validateMission'));
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Inicializar datos cuando se abre el modal (ARREGLADO PARA CLONADO)
  useEffect(() => {
    if (open) {
      setValidationErrors([]);
      setShowValidationErrors(false);
      setIsDragOver(false);

      // Competencias predeterminadas: vienen con el cargo al editar/ver/clonar;
      // vacías al crear desde cero.
      setSelectedCompetencias(
        selectedPosition && Array.isArray(selectedPosition.competencias)
          ? selectedPosition.competencias.map(normalizeCompetencia)
          : []
      );

      if (mode === 'create' && !selectedPosition) {
        // Crear nuevo cargo completamente vacío
        setFormData({
          name: '',
          industria: '',
          descripcion: '',
          jerarquia: '',
          responsabilidades: '',
          perfil: '',
          conocimientosExcluyentes: '',
          conocimientosDeseables: '',
          salarioOfrecido: '' // AGREGADO
        });
      } else if (selectedPosition) {
        // Editar, ver, o clonar (cuando selectedPosition tiene datos)
        setFormData({
          name: selectedPosition.name || '',
          industria: selectedPosition.industria || '',
          descripcion: selectedPosition.descripcion || '',
          jerarquia: selectedPosition.jerarquia || '',
          responsabilidades: selectedPosition.responsabilidades || '',
          perfil: selectedPosition.perfil || '',
          conocimientosExcluyentes: selectedPosition.conocimientosExcluyentes || '',
          conocimientosDeseables: selectedPosition.conocimientosDeseables || '',
          salarioOfrecido: selectedPosition.salarioOfrecido || '' // AGREGADO
        });
      } else {
        // Fallback para crear
        setFormData({
          name: '',
          industria: '',
          descripcion: '',
          jerarquia: '',
          responsabilidades: '',
          perfil: '',
          conocimientosExcluyentes: '',
          conocimientosDeseables: '',
          salarioOfrecido: '' // AGREGADO
        });
      }
    }
  }, [open, mode, selectedPosition]);

  const handleClose = () => {
    setValidationErrors([]);
    setShowValidationErrors(false);
    setIsDragOver(false);
    onClose();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (showValidationErrors) {
      setShowValidationErrors(false);
    }
  };

  const handleSubmit = async () => {
    // Validar solo al intentar guardar
    if (!validateForm()) {
      setShowValidationErrors(true);
      return;
    }

    try {
      setLoading(true);
      let response;

      // Limpiar datos antes de enviar
      const cleanData = {
        name: formData.name.trim(),
        industria: formData.industria.trim(),
        descripcion: formData.descripcion.trim(),
        jerarquia: formData.jerarquia.trim(),
        responsabilidades: formData.responsabilidades.trim(),
        perfil: formData.perfil.trim(),
        conocimientosExcluyentes: formData.conocimientosExcluyentes.trim(),
        conocimientosDeseables: formData.conocimientosDeseables.trim(),
        salarioOfrecido: formData.salarioOfrecido.trim() || null, // AGREGADO
        // Competencias predeterminadas del cargo (se precargan en DISC completo y Assessment)
        competencias: selectedCompetencias.map(normalizeCompetencia),
        projectId: project, // Agregar projectId
        // Sincronización Evaluaciones ⇄ ATS: el backend necesita la organización
        // para resolver la plantilla de pipeline predeterminada del proyecto.
        organizationId: organization,
        lastUpdatedBy: user?.email || user?.uid || 'unknown'
      };

      if (mode === 'create') {
        response = await createPosition(cleanData);
      } else if (mode === 'edit' && selectedPosition) {
        delete cleanData.projectId;
        response = await updatePosition(selectedPosition.id || selectedPosition.uid, cleanData);
      }

      if (response.response && response.response.status_response === 'OK') {
        handleClose();
        onSave();
        showAlert(t(mode === 'create' ? 'positions.positionCreated' : 'positions.positionUpdated'), "success");
      } else {
        showAlert(t('positions.positionSaveError'), "error");
        setShowValidationErrors(false);
      }
    } catch (err) {
      showAlert(t('positions.positionSaveError'), "error");
      console.error('Error saving position:', err);
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = formData.name.trim() !== '' && formData.industria.trim() !== '' && formData.descripcion.trim() !== '' && !docxLoading;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'create' && t('positions.newPosition')}
        {mode === 'edit' && t('positions.editPosition')}
        {mode === 'view' && t('positions.showPosition')}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Errores de validación */}
          {showValidationErrors && validationErrors.length > 0 && mode !== 'view' && (
            <Alert severity="error">
              <Typography variant="subtitle2">{t('validationErrors')}:</Typography>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}

          <TextField
            label={`${t('name')} *`}
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            fullWidth
            disabled={mode === 'view'}
            placeholder={t('positions.positionNamePlaceholder')}
          />

          <Autocomplete
            key={`${mode}-${selectedPosition?.uid || selectedPosition?.id || 'new'}`}
            fullWidth
            freeSolo
            options={industriasOptions}
            inputValue={formData.industria || ''}
            onInputChange={(e, newInputValue) => handleInputChange('industria', newInputValue || "")}
            disabled={mode === 'view'}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`${t('industry')} *`}
                placeholder={t('positions.positionIndustryPlaceholder')}
              />
            )}
          />

          <TextField
            label={t('positions.hierarchy')}
            value={formData.jerarquia}
            onChange={(e) => handleInputChange('jerarquia', e.target.value)}
            fullWidth
            disabled={mode === 'view'}
            placeholder={t('positions.hierarchyPlaceholder')}
          />

          <TextField
            label={t('positions.offeredSalary')}
            value={formData.salarioOfrecido}
            onChange={(e) => handleInputChange('salarioOfrecido', e.target.value)}
            fullWidth
            disabled={mode === 'view'}
            placeholder={t('positions.offeredSalaryPlaceholder')}
            helperText={t('positions.offeredSalaryHelper')}
          />

          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle2">{t('positions.mission')} *</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                {mode !== 'view' && formData.name.trim() !== '' && formData.industria.trim() !== '' && formData.jerarquia.trim() !== '' && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AutoAwesome />}
                    onClick={generateDescriptionWithGPT}
                    disabled={gptLoading || docxLoading}
                    sx={{ minWidth: 'auto' }}
                  >
                    {gptLoading ? t('generating') : t('suggestWithAI')}
                  </Button>
                )}
                {mode !== 'view' && (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CloudUpload />}
                      onClick={downloadTemplate}
                      disabled={docxLoading}
                      sx={{ minWidth: 'auto' }}
                    >
                      {t('positions.downloadTemplate')}
                    </Button>
                    <input
                      id="docx-file-input"
                      type="file"
                      accept=".doc,.docx"
                      style={{ display: 'none' }}
                      onChange={handleFileInputChange}
                      disabled={docxLoading}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={docxLoading ? <CircularProgress size={16} /> : <UploadFile />}
                      onClick={() => document.getElementById('docx-file-input').click()}
                      disabled={docxLoading}
                      sx={{ minWidth: 'auto' }}
                    >
                      {docxLoading ? t('processing') : t('positions.loadTemplate')}
                    </Button>
                  </>
                )}
              </Box>
            </Box>

            <TextField
              value={formData.descripcion}
              onChange={(e) => handleInputChange('descripcion', e.target.value)}
              fullWidth
              multiline
              rows={3}
              disabled={mode === 'view' || docxLoading}
              placeholder={
                isDragOver
                  ? t('positions.missionDragOver')
                  : t('positions.missionPlaceholder')
              }
              helperText={
                isDragOver
                  ? t('positions.missionHelperDragOver')
                  : t('positions.missionHelper')
              }
              onDragEnter={mode !== 'view' ? handleTextareaDragEnter : undefined}
              onDragOver={mode !== 'view' ? handleTextareaDragOver : undefined}
              onDragLeave={mode !== 'view' ? handleTextareaDragLeave : undefined}
              onDrop={mode !== 'view' ? handleTextareaDrop : undefined}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: isDragOver ? '#f3f8ff' : 'transparent',
                  borderColor: isDragOver ? '#1976d2' : undefined,
                  '& fieldset': {
                    borderColor: isDragOver ? '#1976d2' : undefined,
                    borderWidth: isDragOver ? '2px' : '1px',
                    borderStyle: isDragOver ? 'dashed' : 'solid',
                  },
                  transition: 'all 0.3s ease',
                }
              }}
            />
          </Box>

          <TextField
            label={t('positions.responsibilities')}
            value={formData.responsabilidades}
            onChange={(e) => handleInputChange('responsabilidades', e.target.value)}
            fullWidth
            multiline
            rows={4}
            disabled={mode === 'view'}
            placeholder={t('positions.responsibilitiesPlaceholder')}
            helperText={t('positions.responsibilitiesHelper')}
          />

          <TextField
            label={t('positions.profile')}
            value={formData.perfil}
            onChange={(e) => handleInputChange('perfil', e.target.value)}
            fullWidth
            multiline
            rows={3}
            disabled={mode === 'view'}
            placeholder={t('positions.profilePlaceholder')}
            helperText={t('positions.profileHelper')}
          />

          <TextField
            label={t('positions.excludingKnowledge')}
            value={formData.conocimientosExcluyentes}
            onChange={(e) => handleInputChange('conocimientosExcluyentes', e.target.value)}
            fullWidth
            multiline
            rows={3}
            disabled={mode === 'view'}
            placeholder={t('positions.excludingKnowledgePlaceholder')}
          />

          <TextField
            label={t('positions.desirableKnowledge')}
            value={formData.conocimientosDeseables}
            onChange={(e) => handleInputChange('conocimientosDeseables', e.target.value)}
            fullWidth
            multiline
            rows={3}
            disabled={mode === 'view'}
            placeholder={t('positions.desirableKnowledgePlaceholder')}
          />

          {/* Competencias predeterminadas del cargo: se precargan al enviar un
              DISC modo completo o al configurar un Assessment para este cargo */}
          <Autocomplete
            multiple
            options={competenciesOptions}
            value={selectedCompetencias}
            onChange={(event, newValue) =>
              setSelectedCompetencias(newValue.map(normalizeCompetencia))
            }
            getOptionLabel={(option) => option.competencyName ?? option.name ?? ''}
            isOptionEqualToValue={(option, value) =>
              (option.competencyId ?? option.id ?? option.uid) ===
              (value.competencyId ?? value.id ?? value.uid)
            }
            loading={loadingCompetencies}
            disabled={mode === 'view'}
            renderOption={(props, option) => (
              <li {...props} key={option.id ?? option.uid}>
                {option.name}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option.competencyId}
                  label={option.competencyName}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('positions.defaultCompetencies')}
                helperText={t('positions.defaultCompetenciesHelper')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCompetencies ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {mode === 'view' ? t('close') : t('cancel')}
        </Button>
        {mode !== 'view' && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !canSubmit}
          >
            {t('save')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PositionsModal;